// Matching rules mirrored from zippedpdfbulkupload.js (2026-10-21b).
// Keep these functions in sync if the backend parsing rules change.
const FACILITY_MATCH_THRESHOLD = 0.60;
const FACILITY_AMBIGUITY_DIFFERENCE = 0.08;
const FACILITY_STOP_WORDS = new Set([
    "AND", "THE", "OF", "AT", "FOR", "IN", "A", "AN",
    "CENTER", "CENTRE", "HEALTH", "HEALTHCARE", "NURSING",
    "REHABILITATION", "REHAB", "CARE", "FACILITY",
    "SERVICES", "SERVICE", "LLC", "INC", "CORP", "CORPORATION",
]);
const CORPORATION_STOP_WORDS = new Set([
    "HEALTH", "HEALTHCARE", "MANAGEMENT", "SERVICES", "SERVICE",
    "GROUP", "HOLDINGS", "FACILITIES", "FACILITY", "LLC", "INC",
    "CORP", "CORPORATION", "COMPANY", "CO",
]);

function normalizeZipPath(value) {
    return String(value || "")
        .replace(/^\uFEFF/, "")
        .replace(/\\/g, "/")
        .replace(/^\/+/, "")
        .replace(/\/+/g, "/")
        .trim();
}

function normalizeName(value) {
    return String(value || "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/&/g, " AND ")
        .replace(/[^A-Za-z0-9]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toUpperCase();
}

function getMeaningfulTokens(value) {
    return normalizeName(value)
        .split(" ")
        .filter(Boolean)
        .filter((token) => !FACILITY_STOP_WORDS.has(token));
}

function removeCorporationWords(facilityName, corporationName) {
    const facilityTokens = normalizeName(facilityName)
        .split(" ")
        .filter(Boolean);

    const corporationTokens = new Set(
        normalizeName(corporationName)
            .split(" ")
            .filter(Boolean)
    );

    return facilityTokens
        .filter((token) => !corporationTokens.has(token))
        .join(" ");
}

function calculateTokenSimilarity(leftValue, rightValue) {
    const leftTokens = new Set(getMeaningfulTokens(leftValue));
    const rightTokens = new Set(getMeaningfulTokens(rightValue));

    if (leftTokens.size === 0 || rightTokens.size === 0) {
        return 0;
    }

    let intersectionCount = 0;

    for (const token of leftTokens) {
        if (rightTokens.has(token)) {
            intersectionCount += 1;
        }
    }

    const unionCount =
        leftTokens.size +
        rightTokens.size -
        intersectionCount;

    const jaccard =
        unionCount > 0
            ? intersectionCount / unionCount
            : 0;

    const shorterSize = Math.min(
        leftTokens.size,
        rightTokens.size
    );

    const coverage =
        shorterSize > 0
            ? intersectionCount / shorterSize
            : 0;

    return Math.max(jaccard, coverage * 0.9);
}

// Some historical filenames begin the facility with (West) or (East), while
// the reference database uses the actual facility name without that label.
// Strip only a *leading* directional tag for a second search; never alter the
// original pdf_path or source object key.

function stripDirectionalFacilityPrefix(value) {
    return String(value || "")
        .trim()
        // Some filenames use "(East)" and others use just "East".
        // Only remove it at the beginning, never from the actual filename.
        .replace(/^(?:\(\s*(?:WEST|EAST)\s*\)|(?:WEST|EAST)\b)\s*/i, "")
        .trim();
}

function stripTrailingCorporationFromFacility(value, corporationName) {
    const tokens = String(value || "").trim().split(/\s+/).filter(Boolean);
    const corporationTokens = normalizeName(corporationName).split(" ").filter(Boolean);
    if (!corporationTokens.length || tokens.length <= corporationTokens.length) {
        return String(value || "").trim();
    }
    const suffix = tokens.slice(-corporationTokens.length)
        .map((token) => normalizeName(token));
    if (suffix.join(" ") !== corporationTokens.join(" ")) {
        return String(value || "").trim();
    }
    return tokens.slice(0, -corporationTokens.length).join(" ");
}

// Generate aliases for comparison only. In particular, "East Five Points"
// may be a document-region label, and "University Park ... Creative Solutions"
// may include the corporation name after the facility. Neither changes the
// original PDF filename, MinIO object key, or database name.

function getFacilityNameVariants(value, corporationName = "") {
    const original = String(value || "").replace(/\s+/g, " ").trim();
    const variants = [];
    const seen = new Set();
    const add = (name) => {
        const cleaned = String(name || "").replace(/\s+/g, " ").trim();
        const identity = normalizeName(cleaned);
        if (identity && !seen.has(identity)) {
            seen.add(identity);
            variants.push(cleaned);
        }
    };
    const expand = (operation) => {
        for (const existing of [...variants]) {
            add(operation(existing));
        }
    };

    add(original);
    expand(stripDirectionalFacilityPrefix);
    expand((name) => stripTrailingCorporationFromFacility(name, corporationName));
    expand((name) => name.replace(/^THE\s+/i, ""));
    expand((name) => name.replace(/(?:\s*-\s*|\s+)SNF$/i, ""));
    return variants;
}

// /search_orgs4 performs text filtering before our local fuzzy scoring.
// Query full names first, then a short, distinctive phrase if punctuation,
// suffixes, or database naming differences prevent a full-text hit.

function getFacilitySearchTerms(value, corporationName = "") {
    const terms = [];
    const seen = new Set();
    const add = (query) => {
        const trimmed = String(query || "").replace(/\s+/g, " ").trim();
        const identity = normalizeName(trimmed);
        if (trimmed.length >= 5 && !seen.has(identity)) {
            seen.add(identity);
            terms.push(trimmed);
        }
    };

    const variants = getFacilityNameVariants(value, corporationName);
    variants.forEach(add);
    const canonical = variants[variants.length - 1] || "";
    const words = canonical.split(/\s+/).filter(Boolean);
    const meaningful = words.filter(word => !FACILITY_STOP_WORDS.has(normalizeName(word)));
    if (meaningful.length >= 2) {
        add(meaningful.slice(0, 3).join(" "));
        add(meaningful.slice(0, 2).join(" "));
    }
    if (words.length > 3) {
        add(words.slice(0, Math.max(2, words.length - 1)).join(" "));
        add(words.slice(0, 2).join(" "));
    }
    return terms.slice(0, 10);
}

function calculateFacilityNameScore(
    extractedFacilityName,
    candidateFacilityName,
    corporationName
) {
    const extracted = normalizeName(
        extractedFacilityName
    );

    const candidate = normalizeName(
        candidateFacilityName
    );

    if (!extracted || !candidate) {
        return {
            score: 0,
            method: "empty_name",
        };
    }

    if (extracted === candidate) {
        return {
            score: 1,
            method: "exact",
        };
    }

    if (
        extracted.includes(candidate) ||
        candidate.includes(extracted)
    ) {
        const shorter = Math.min(
            extracted.length,
            candidate.length
        );

        const longer = Math.max(
            extracted.length,
            candidate.length
        );

        return {
            score:
                0.88 +
                (longer > 0
                    ? (shorter / longer) * 0.1
                    : 0),
            method: "contained_name",
        };
    }

    const fullScore = calculateTokenSimilarity(
        extractedFacilityName,
        candidateFacilityName
    );

    const strippedScore = calculateTokenSimilarity(
        removeCorporationWords(
            extractedFacilityName,
            corporationName
        ),
        removeCorporationWords(
            candidateFacilityName,
            corporationName
        )
    );

    return {
        score: Math.max(fullScore, strippedScore),
        method:
            strippedScore > fullScore
                ? "token_match_without_corporation"
                : "token_match",
    };
}

function scoreFacilityCandidate({
                                    extractedFacilityName,
                                    corporationName,
                                    candidate,
                                }) {
    const names = [
        {
            field: "facility_name",
            value: candidate.facility_name,
        },
        {
            field: "monday_facility_name",
            value: candidate.monday_facility_name,
        },
    ].filter((item) => String(item.value || "").trim());

    let best = {
        score: 0,
        method: "no_name",
        matchedField: null,
        matchedName: null,
    };

    for (const item of names) {
        const result = calculateFacilityNameScore(
            extractedFacilityName,
            item.value,
            corporationName
        );

        if (result.score > best.score) {
            best = {
                score: result.score,
                method: result.method,
                matchedField: item.field,
                matchedName: item.value,
            };
        }
    }

    return {
        candidate,
        score: Number(best.score.toFixed(4)),
        matchMethod: best.method,
        matchedField: best.matchedField,
        matchedName: best.matchedName,
    };
}

function findBestFacilityMatch({
                                   corporationName,
                                   facilityName,
                                   searchResults,
                               }) {
    if (!Array.isArray(searchResults) || searchResults.length === 0) {
        return {
            status: "not_found",
            reason:
                "No organization records were returned.",
            confidence: 0,
            match: null,
            possibleMatches: [],
        };
    }

    // Multiple source-system rows can represent the same facility. Score
    // every alias first, then keep the highest scoring one per facility ID.
    // Otherwise duplicate rows create false ambiguity and the first alias
    // can hide the better matching Monday/facility name on a later row.
    const byFacility = new Map();
    for (const candidate of searchResults) {
        const scoredCandidate = scoreFacilityCandidate({
            extractedFacilityName: facilityName,
            corporationName,
            candidate,
        });
        const facilityIdentity = String(candidate.facility_unique_id || [
            candidate.fac_id || "",
            candidate.ouid || "",
            candidate.ehr_ds || "",
            normalizeName(candidate.monday_facility_name || candidate.facility_name),
        ].join("|"));
        const existing = byFacility.get(facilityIdentity);
        if (!existing || scoredCandidate.score > existing.score) {
            byFacility.set(facilityIdentity, scoredCandidate);
        }
    }
    const scored = [...byFacility.values()].sort((a, b) => b.score - a.score);

    const best = scored[0];
    const second = scored[1];

    const possibleMatches = scored
        .slice(0, 3)
        .map((item) => ({
            score: item.score,
            matchMethod: item.matchMethod,
            matchedName: item.matchedName,
            facility_unique_id:
            item.candidate.facility_unique_id,
            facility_name:
            item.candidate.facility_name,
            monday_facility_name:
            item.candidate.monday_facility_name,
            corp_name:
            item.candidate.corp_name,
            source_id:
            item.candidate.source_id,
        }));

    if (
        !best ||
        best.score < FACILITY_MATCH_THRESHOLD
    ) {
        return {
            status: "not_found",
            reason:
                "No facility candidate reached the minimum score.",
            confidence: best?.score || 0,
            match: null,
            possibleMatches,
        };
    }

    // An exact normalized facility-name match is decisive if exactly one
    // distinct facility ID has it. Do not reject a perfect match merely
    // because another facility has the same words plus an extra location
    // (e.g. Five Points Nursing vs Five Points Nursing DeSoto).
    const exactMatches = scored.filter((item) =>
        item.score === 1 && item.matchMethod === "exact"
    );
    if (exactMatches.length === 1) {
        const exact = exactMatches[0];
        return {
            status: "matched",
            reason: null,
            confidence: 1,
            matchMethod: "exact",
            matchedField: exact.matchedField,
            matchedName: exact.matchedName,
            match: exact.candidate,
            possibleMatches: [],
        };
    }
    // Two *different* facility IDs with exact names must still be reviewed.
    if (exactMatches.length > 1) {
        return {
            status: "ambiguous",
            reason: "Multiple distinct facilities share an exact normalized name.",
            confidence: 1,
            match: null,
            possibleMatches,
        };
    }

    if (
        second &&
        best.score - second.score <
        FACILITY_AMBIGUITY_DIFFERENCE
    ) {
        return {
            status: "ambiguous",
            reason:
                "Multiple facility candidates received similar scores.",
            confidence: best.score,
            match: null,
            possibleMatches,
        };
    }

    return {
        status: "matched",
        reason: null,
        confidence: best.score,
        matchMethod: best.matchMethod,
        matchedField: best.matchedField,
        matchedName: best.matchedName,
        match: best.candidate,
        possibleMatches: [],
    };
}

// Preserve existing threshold and ambiguity requirements while trying
// spelling variations that don't necessarily appear verbatim in the MDM.

function findFacilityMatchWithDirectionFallback(args) {
    const variants = getFacilityNameVariants(args.facilityName, args.corporationName);
    const attempts = variants.map((variant) => {
        const result = findBestFacilityMatch({ ...args, facilityName: variant });
        return {
            ...result,
            matchedVariant: variant,
            isOriginalVariant: variant === variants[0],
        };
    });

    // Look for a unique exact match across original and cleaned names BEFORE
    // returning an ambiguous fuzzy result. Example: "(East) UNIVERSITY PARK
    // ... Creative Solutions" becomes an exact University Park name after
    // stripping the prefix and corporation suffix.
    const exactById = new Map();
    for (const attempt of attempts) {
        if (attempt.status === "matched" && attempt.matchMethod === "exact") {
            const id = String(attempt.match.facility_unique_id || [
                attempt.match.fac_id || "",
                attempt.match.ouid || "",
                normalizeName(attempt.match.facility_name || ""),
            ].join("|"));
            if (!exactById.has(id)) exactById.set(id, attempt);
        }
    }
    if (exactById.size === 1) {
        const selected = [...exactById.values()][0];
        return {
            ...selected,
            matchMethod: selected.isOriginalVariant
                ? "exact"
                : "exact_facility_variant",
        };
    }
    if (exactById.size > 1) {
        return {
            status: "ambiguous",
            reason: "Different facilities matched different exact filename variants.",
            confidence: 1,
            match: null,
            possibleMatches: [...exactById.values()].slice(0, 3).map((item) => ({
                facility_unique_id: item.match.facility_unique_id,
                facility_name: item.match.facility_name,
                monday_facility_name: item.match.monday_facility_name,
                corp_name: item.match.corp_name,
                score: 1,
                matchMethod: "exact",
                matchedName: item.matchedName,
                source_id: item.match.source_id,
            })),
        };
    }

    // Preserve the original matching decision if no exact cleaned variant
    // exists. Fuzzy ambiguity still blocks automatic consent creation.
    const original = attempts[0];
    if (original.status === "matched") return original;
    const otherMatched = attempts.slice(1).filter((attempt) => attempt.status === "matched")
        .sort((a, b) => b.confidence - a.confidence);
    if (otherMatched.length) {
        const selected = otherMatched[0];
        return { ...selected, matchMethod: `${selected.matchMethod}_facility_variant` };
    }
    const ambiguous = attempts.filter((attempt) => attempt.status === "ambiguous")
        .sort((a, b) => b.confidence - a.confidence);
    if (ambiguous.length) return ambiguous[0];
    return attempts.sort((a, b) => b.confidence - a.confidence)[0];
}

function getCorporationSearchTerms(corporationName) {
    const terms = [];

    const add = (value) => {
        const trimmed = String(value || "").trim();

        if (
            trimmed.length >= 2 &&
            !terms.some(
                (existing) =>
                    normalizeName(existing) ===
                    normalizeName(trimmed)
            )
        ) {
            terms.push(trimmed);
        }
    };

    // Search the corporation only. Facility matching is performed locally
    // against the full corporation result set returned by search_orgs4.
    add(corporationName);

    const originalTokens = String(corporationName || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);

    const withoutLegalSuffixes = originalTokens.filter(
        (token) =>
            !["LLC", "INC", "CORP", "CORPORATION", "CO", "COMPANY"]
                .includes(normalizeName(token))
    );

    if (withoutLegalSuffixes.length >= 1) {
        add(withoutLegalSuffixes.join(" "));
    }

    const meaningfulTokens = originalTokens.filter(
        (token) =>
            !CORPORATION_STOP_WORDS.has(normalizeName(token))
    );

    if (meaningfulTokens.length >= 1) {
        add(meaningfulTokens.join(" "));
    }

    return terms;
}

function calculateCorporationNameScore(
    extractedCorporationName,
    candidateCorporationName
) {
    const extracted = normalizeName(extractedCorporationName);
    const candidate = normalizeName(candidateCorporationName);

    if (!extracted || !candidate) {
        return 0;
    }

    if (extracted === candidate) {
        return 1;
    }

    if (
        extracted.includes(candidate) ||
        candidate.includes(extracted)
    ) {
        const shorter = Math.min(
            extracted.length,
            candidate.length
        );

        const longer = Math.max(
            extracted.length,
            candidate.length
        );

        return 0.9 + (
            longer > 0
                ? (shorter / longer) * 0.09
                : 0
        );
    }

    return calculateTokenSimilarity(
        extractedCorporationName,
        candidateCorporationName
    );
}

function filterCandidatesToBestCorporation({
                                               corporationName,
                                               searchResults,
                                           }) {
    if (!Array.isArray(searchResults) || searchResults.length === 0) {
        return {
            corporationName: null,
            corporationUniqueId: null,
            confidence: 0,
            candidates: [],
        };
    }

    const corporationGroups = new Map();

    for (const candidate of searchResults) {
        const groupKey = String(
            candidate.corp_unique_id ||
            normalizeName(candidate.corp_name)
        );

        if (!corporationGroups.has(groupKey)) {
            corporationGroups.set(groupKey, {
                corp_unique_id:
                    candidate.corp_unique_id || null,
                corp_name:
                    candidate.corp_name || "",
                rows: [],
            });
        }

        corporationGroups.get(groupKey).rows.push(candidate);
    }

    const scoredGroups = [...corporationGroups.values()]
        .map((group) => ({
            ...group,
            score: calculateCorporationNameScore(
                corporationName,
                group.corp_name
            ),
        }))
        .sort((a, b) => b.score - a.score);

    const best = scoredGroups[0];

    if (!best || best.score < 0.60) {
        return {
            corporationName: null,
            corporationUniqueId: null,
            confidence: best?.score || 0,
            candidates: [],
        };
    }

    return {
        corporationName: best.corp_name,
        corporationUniqueId:
        best.corp_unique_id,
        confidence:
            Number(best.score.toFixed(4)),
        candidates: best.rows,
    };
}

function parsePdfPath(pdfPath, options = {}) {
    const normalizedPath = normalizeZipPath(pdfPath);
    const parts = normalizedPath.split("/").filter(Boolean);
    const filename = parts.pop() || "";
    const corporationName = parts.length > 0 ? String(parts[0]).trim() : "";
    const withoutExtension = filename.replace(/\.pdf$/i, "").trim();

    // Usually the first '(' starts the facility. But a surname may have an
    // alias in parentheses before the comma, e.g. "Seward (Harvey), Shari
    // (ARBORETUM...)". In that case the facility starts at the first '(' AFTER
    // the patient's surname/given-name comma. Preserve the parenthetical
    // surname because the patient lookup may need that alias.
    const firstOpeningParen = withoutExtension.indexOf("(");
    const firstComma = withoutExtension.indexOf(",");
    const facilityOpeningParen = firstOpeningParen >= 0 &&
    firstComma > firstOpeningParen
        ? withoutExtension.indexOf("(", firstComma + 1)
        : firstOpeningParen;
    if (facilityOpeningParen < 0) {
        return {
            valid: false,
            error: "PDF filename must contain 'Last, First (Facility)'.",
            pdfPath: normalizedPath,
        };
    }

    const patientName = withoutExtension
        .slice(0, facilityOpeningParen)
        .replace(/\s+(?:#\s*)?\d+\s*$/, "") // MARIA 1 or VALETA M. #2
        .replace(/\s+/g, " ")
        .trim();

    let facilityName = withoutExtension.slice(facilityOpeningParen).trim();
    // Remove report copy numbers like " (4)" at the very end.
    facilityName = facilityName.replace(/\s+\(\s*#?\d+\s*\)\s*$/i, "");
    // Remove a PDF suffix after a closing bracket, e.g. ')1', ') 2', ') 2 - 2 OF 3'.
    facilityName = facilityName.replace(/(\))\s*\d+(?:\s*-\s*\d+)?(?:\s+OF\s+\d+)?\s*$/i, "$1");

    // Accept an extra unmatched ')' at the very end without cutting off (West).
    let balance = 0;
    for (const character of facilityName) {
        if (character === "(") balance += 1;
        else if (character === ")") balance -= 1;
    }
    while (balance < 0 && facilityName.endsWith(")")) {
        facilityName = facilityName.slice(0, -1).trim();
        balance += 1;
    }

    // Unwrap only when the first '(' pairs with the final ')'. This retains
    // the meaningful '(West)' prefix on facilities such as (West) VISTA HILLS.
    let depth = 0;
    let firstPairEnd = -1;
    for (let index = 0; index < facilityName.length; index += 1) {
        if (facilityName[index] === "(") depth += 1;
        else if (facilityName[index] === ")") {
            depth -= 1;
            if (depth === 0) {
                firstPairEnd = index;
                break;
            }
        }
    }
    if (firstPairEnd === facilityName.length - 1) {
        facilityName = facilityName.slice(1, -1);
    } else if (firstPairEnd === -1 && facilityName.startsWith("((")) {
        // Malformed wrapper missing its final ')' around a nested facility.
        facilityName = facilityName.slice(1);
    }
    facilityName = facilityName.replace(/\s+/g, " ").trim();

    if (!corporationName) {
        return {
            valid: false,
            error: "PDF path does not contain a corporation folder.",
            pdfPath: normalizedPath,
        };
    }
    // Prefer a comma delimiter. Historical documents sometimes use a period
    // followed by whitespace between surname and given name instead.
    const commaIndex = patientName.indexOf(",");
    const periodSeparator = commaIndex < 0 ? /\.\s+(?=\S)/.exec(patientName) : null;
    const separatorIndex = commaIndex >= 0 ? commaIndex : periodSeparator?.index ?? -1;
    let patientLastName;
    let patientFirstName;
    let patientNameError = "";
    if (separatorIndex >= 0) {
        patientLastName = patientName.slice(0, separatorIndex).trim();
        patientFirstName = patientName.slice(separatorIndex + 1).trim();
    } else {
        // Older filenames occasionally omit the comma: "Eades Melvin".
        // Interpret only unambiguous TWO-word entries as Last First. Never
        // guess the split of three or more words (compound surnames/names).
        const nameWords = patientName.split(/\s+/).filter(Boolean);
        if (nameWords.length !== 2 ||
            nameWords.some(word => normalizeName(word).length < 2)) {
            patientNameError = "Cannot safely split patient name without a comma or period. " +
                "Confirm the first and last names or exclude this PDF row.";
            if (!options.allowInvalidPatientName) {
                return { valid: false, error: patientNameError, pdfPath: normalizedPath };
            }
            patientLastName = "";
            patientFirstName = "";
        } else {
            [patientLastName, patientFirstName] = nameWords;
        }
    }
    if ((!patientFirstName || !patientLastName) && !patientNameError || !facilityName) {
        return {
            valid: false,
            error: "Patient or facility name could not be parsed.",
            pdfPath: normalizedPath,
        };
    }

    return {
        valid: true,
        pdfPath: normalizedPath,
        corporationName,
        facilityName,
        patientFirstName,
        patientLastName,
        rawPatientName: patientName,
        patientNameError,
    };
}

export {
    parsePdfPath,
    normalizeName,
    getFacilityNameVariants,
    getFacilitySearchTerms,
    calculateCorporationNameScore,
    filterCandidatesToBestCorporation,
    findFacilityMatchWithDirectionFallback,
};
